import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import _ from 'lodash';
import { useSnapshot, subscribe } from 'valtio';
import { Spinner } from 'reactstrap';
import { Stepper } from 'components/ui/Stepper';
import Portfolio from './Portfolio';
import Assumptions from './Assumptions';
import Groups from './Groups';
import ModelingAssumptions from './ModelingAssumptions';
import { optimizationState } from '../state';
import {
  getQuickViewDiscovery,
  getPortfolioOptimizerOptions,
  getPortfolioOptimizerResult as _getPortfolioOptimizerResult,
} from 'services/analysis';
import { trimData, percentToFloat, zipToObject, parseNumericValue } from './Shared';

function getErrorMessage(error) {
  const paths = ['response.data.detail', 'response.data.message', 'message'];
  for (const path of paths) {
    const message = _.get(error, path);
    if (message) {
      return message;
    }
  }

  return String(error);
}

const unsubscribe = subscribe(optimizationState, async () => {
  const snap = optimizationState;
  if (snap.status !== 'complete') return;

  const { products, databases, weights, expectedReturns, minWeights, maxWeights, groups } = trimData(
    snap.products,
    snap.databases,
    snap.weights,
    snap.expectedReturns,
    snap.minWeights,
    snap.maxWeights,
    snap.groups,
  );

  const defaultLength = products.length;
  const historical = snap.returnAssumption === 'Historical';

  const groupConstraints = {};
  for (const [key, value] of Object.entries(snap.groupNames)) {
    if (value) {
      groupConstraints[value] = [
        percentToFloat(snap.groupMinWeights[key] ?? 0),
        percentToFloat(snap.groupMaxWeights[key] ?? 100),
      ];
    }
  }

  const args = {
    product: products,
    database: databases,
    target: percentToFloat(snap.targetReturn),
    er_params: { [historical ? 'historical' : 'user_input']: products },
    constraints: {
      lb: snap.assetConstraint ? minWeights.map(percentToFloat) : Array(defaultLength).fill(0),
      ub: snap.assetConstraint ? maxWeights.map(percentToFloat) : Array(defaultLength).fill(1),
    },
    user_input_returns: historical
      ? {}
      : zipToObject(products, expectedReturns, (v) => parseNumericValue(v, -100, 100)),
    group_params: snap.groupConstraint
      ? {
          mapper: zipToObject(products, groups, (v) => snap.groupNames[v]),
          group_constraints: groupConstraints,
        }
      : {},
    covariance_period: snap.timePeriod,
    sig_level: snap.confidence,
    user_rm: 0,
    user_rfr: 0,
    beta_period: 0,
    benchmark: snap.benchmark,
    objective: snap.optimizationGoal,
    converter: snap.currency,
    user_port_dict: zipToObject(products, weights, (v) => percentToFloat(v)),
    pc_params_dict: {
      fee: 0,
      rebalance: 'Annual',
      accrual: 'Annual',
      deduction: 'Annual',
      structure: 'General',
    },
    user_port_name: snap.portName,
    optimized_port_name: snap.portName,
    sdate: snap.sdate,
    edate: '',
  };

  optimizationState.status = 'draft';
  try {
    const result = await _getPortfolioOptimizerResult(args);
    optimizationState.result = result;
    optimizationState.navigate('/pa/portfolio-optimization/result', { replace: false });
  } catch (error) {
    optimizationState.result = null;
    optimizationState.notify.danger({ message: <div>{getErrorMessage(error)}</div> });
  }
});

function PortfolioOptimizationBuild({ confirm, notify, session }) {
  const snap = useSnapshot(optimizationState);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const {
    data: productMeta,
    isLoading: isProductMetaLoading,
    isError: isProductMetaError,
    error: productMetaError,
    refetch: refetchProductMeta,
  } = useQuery({
    queryKey: ['productMeta'],
    queryFn: () => getQuickViewDiscovery().then((res) => res[0]),
  });

  const {
    data: optimizerOptions,
    isLoading: isOptimizerOptionsLoading,
    isError: isOptimizerOptionsError,
    error: optimizerOptionsError,
    refetch: refetchOptimizerOptions,
  } = useQuery({
    queryKey: ['optimizerOptions'],
    queryFn: () => getPortfolioOptimizerOptions(),
  });

  if (isProductMetaLoading || isOptimizerOptionsLoading || _.isEmpty(productMeta)) {
    return (
      <div className="tw-text-center">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  let isNextEnabled = true;
  if (currentStep === 0 || currentStep === 2) {
    isNextEnabled = snap.isValid;
  }

  const stepComponents = [
    <Portfolio productMeta={productMeta} session={session} confirm={confirm} />,
    <Assumptions optimizerOptions={optimizerOptions} />,
    <Portfolio productMeta={productMeta} session={session} confirm={confirm} extended />,
  ];

  if (snap.groupConstraint) {
    stepComponents.push(<Groups />);
  }

  stepComponents.push(<ModelingAssumptions optimizerOptions={optimizerOptions} />);

  const steps = stepComponents.map((comp, i) => {
    return { title: `Step ${i + 1}` };
  });

  return (
    <div className="tw-container tw-mx-auto tw-py-4">
      {/* <h1 className="tw-text-2xl tw-font-bold tw-mb-0 tw-text-center">Portfolio Optimization</h1> */}
      <Stepper
        steps={steps}
        currentStep={currentStep}
        onStepChange={async (step) => {
          if (step === steps.length) {
            optimizationState.status = 'complete';
            optimizationState.notify = notify;
            optimizationState.navigate = navigate;
          } else {
            setCurrentStep(step);
          }
        }}
        isNextEnabled={isNextEnabled}
        lastText="RUN OPTIMIZATION"
      >
        <div>
          {stepComponents.map((comp, i) => {
            return (
              <div key={i} className={currentStep === i ? 'tw-block' : 'tw-hidden'}>
                {comp}
              </div>
            );
          })}
        </div>
      </Stepper>
    </div>
  );
}

export default PortfolioOptimizationBuild;
